import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/format';

type BalanceCardProps = {
  balance: number;
  currency: string;
  onWithdraw: () => void;
};

export default function BalanceCard({ balance, currency, onWithdraw }: BalanceCardProps) {
  const { t } = useTranslation();

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-primary-600 via-primary-700 to-primary-900 p-6 md:p-8">
      {/* Vague claire incurvée en haut de la carte */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-[38%] -top-[58%] h-[125%] w-[115%] rotate-[14deg] rounded-[50%] bg-primary-400/25"></div>
        <div className="absolute -left-[22%] top-[38%] h-[75%] w-[85%] rotate-[-10deg] rounded-[50%] bg-primary-500/20"></div>
      </div>

      {/* Effet bokeh (petits ronds lumineux) */}
      <div className="pointer-events-none absolute inset-0">
        <span className="absolute right-10 top-10 h-2.5 w-2.5 rounded-full bg-accent-200/40"></span>
        <span className="absolute right-24 top-24 h-1.5 w-1.5 rounded-full bg-background-50/30"></span>
        <span className="absolute right-16 top-40 h-2 w-2 rounded-full bg-background-50/20"></span>
        <span className="absolute bottom-24 right-28 h-3 w-3 rounded-full bg-accent-300/25"></span>
        <span className="absolute bottom-14 right-12 h-1.5 w-1.5 rounded-full bg-background-50/25"></span>
        <span className="absolute left-1/3 top-16 h-2 w-2 rounded-full bg-background-50/15"></span>
        <span className="absolute bottom-32 left-10 h-2.5 w-2.5 rounded-full bg-primary-300/30"></span>
      </div>

      <div className="relative z-10">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50/15 text-background-50">
            <i className="ri-wallet-3-line text-lg"></i>
          </span>
          <span className="text-sm font-semibold uppercase tracking-[0.14em] text-accent-200">
            {t('dashboard.balanceLabel')}
          </span>
        </div>

        <p className="mt-6 font-heading text-4xl font-bold tracking-tight text-background-50 md:text-5xl">
          {formatCurrency(balance, currency)}
        </p>
        <p className="mt-3 text-sm text-background-50/80 md:text-base">
          {t('dashboard.balanceHint')}
        </p>

        <button
          type="button"
          onClick={onWithdraw}
          className="mt-8 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-background-50 px-5 py-3.5 text-sm font-semibold text-primary-800 transition-colors hover:bg-primary-50 md:text-base"
        >
          <i className="ri-download-2-line text-lg"></i>
          {t('dashboard.withdraw')}
        </button>
      </div>
    </div>
  );
}